/**
 * useActivityManageDiff
 *
 * Owns the manage/edit derived state for the activity signup modal: which slots the viewer already
 * holds, the add/cancel/guest-edit diff against the current form selection, the "+1 guests" baseline
 * (seeded once per open from the held registration), and the won't-fit advisory. Extracted from
 * ActivitySignupModal so the modal renders the diff instead of computing it inline.
 *
 * The hook owns the seed lifecycle (loading the viewer's existing guests) and the baseline used to
 * detect guest changes; it pushes the seeded names back into the form via `onGuestsSeeded` so the
 * shared guest-count/guest-names form state stays the single source of truth for editing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ActivityRow, ActivityTimeSlotRow } from "../_logic/types/db-types";
import type { SlotRegistrationCounts } from "../_logic/actions/signup-actions";
import { getMyRegistrationGuestsFromSession } from "../_logic/actions/manage-registration-guests-actions";

/** Lifecycle of the guest-seed fetch. `loaded` is the gate that lets guest editing be offered. */
export type GuestSeedStatus = "idle" | "loading" | "loaded" | "error";

interface UseActivityManageDiffParams {
  activity: ActivityRow;
  /** This activity's bookable slots (already filtered by the caller). */
  bookableSlots: ActivityTimeSlotRow[];
  slotCounts: Record<number, SlotRegistrationCounts>;
  /** slotId -> registration row id, for the slots the viewer currently holds. */
  registrationBySlotId: Map<number, number>;
  /** The form's current slot selection. */
  selectedSlotIds: number[];
  /** The form's current "+1 guests" count + names (shared with the signup path). */
  guestCount: number;
  guestNames: string[];
  showToast: (message: string, type: "success" | "error") => void;
  /** Push the seeded existing guests into the form's guest fields (once, after a successful read). */
  onGuestsSeeded: (names: string[]) => void;
}

interface UseActivityManageDiffResult {
  /** This activity's bookable slots the viewer currently holds AND can cancel. */
  registeredSlotIds: Set<number>;
  /** True when the viewer holds ≥1 slot of this activity (gates the whole edit/cancel path). */
  canManage: boolean;
  /** True when the manage form should seed + diff guests (manage mode + activity allows +1). */
  needsGuestSeed: boolean;
  /** Selected slots the viewer does NOT yet hold → to register. */
  toAddSlotIds: number[];
  /** Held slots no longer selected → to cancel. */
  toCancelSlotIds: number[];
  /** True when the form's guest list differs from the seeded baseline. */
  guestsChanged: boolean;
  /** Kept registrations (held + still selected) that need the new guest list written. */
  guestUpdateSlotIds: number[];
  /** True when the diff has anything to apply. */
  hasChanges: boolean;
  /** Advisory shown when the party won't fit the seats left on a selected slot. */
  wontFitWarning: string | null;
  /** The viewer's existing guests, fetched on open — the baseline the form diffs against. */
  baselineGuestNames: string[];
  /** Seed fetch status; guest editing is held back until `loaded`. */
  guestSeedStatus: GuestSeedStatus;
  /** Re-point the baseline after a partial reconcile re-syncs committed guest state. */
  applyGuestBaseline: (names: string[]) => void;
}

/**
 * Compute the manage/edit diff + guest baseline for the signup modal.
 *
 * @param params - Activity + slot context, the form's selection/guests, and the seed callback.
 * @returns The held-slot set, the add/cancel/guest diff, the won't-fit advisory, and the guest seed state.
 */
export function useActivityManageDiff(params: UseActivityManageDiffParams): UseActivityManageDiffResult {
  const {
    activity,
    bookableSlots,
    slotCounts,
    registrationBySlotId,
    selectedSlotIds,
    guestCount,
    guestNames,
    showToast,
    onGuestsSeeded,
  } = params;

  // States
  const [baselineGuestNames, setBaselineGuestNames] = useState<string[]>([]);
  const [guestSeedStatus, setGuestSeedStatus] = useState<GuestSeedStatus>("idle");
  // Guard so the async seed runs at most once per registration — a parent count-refresh that swaps
  // the registrationBySlotId Map identity must not re-fire the seed and clobber in-progress edits.
  const seededRegistrationIdRef = useRef<number | null>(null);

  // Derived values — held slots. registeredSlotIds are this activity's bookable slots the viewer
  // currently holds AND can cancel (we have the registration id). Non-empty only for session-proven
  // viewers, so the whole edit/cancel path is gated to them.
  const registeredSlotIds = useMemo(() => {
    const ids = new Set<number>();
    for (const s of bookableSlots) if (registrationBySlotId.has(s.id)) ids.add(s.id);
    return ids;
  }, [bookableSlots, registrationBySlotId]);
  const canManage = registeredSlotIds.size > 0;

  // Registration to read existing guests from when seeding. Guests are applied uniformly across a
  // viewer's slots at signup, so any held slot's row reflects the current set.
  const seedRegistrationId = useMemo(() => {
    for (const slotId of registeredSlotIds) {
      const id = registrationBySlotId.get(slotId);
      if (id != null) return id;
    }
    return null;
  }, [registeredSlotIds, registrationBySlotId]);

  // Derived values — slot diff
  const selectedSet = useMemo(() => new Set(selectedSlotIds), [selectedSlotIds]);
  const toAddSlotIds = useMemo(
    () => selectedSlotIds.filter((id) => !registeredSlotIds.has(id)),
    [selectedSlotIds, registeredSlotIds]
  );
  const toCancelSlotIds = useMemo(
    () => Array.from(registeredSlotIds).filter((id) => !selectedSet.has(id)),
    [registeredSlotIds, selectedSet]
  );

  // Derived values — guest diff. The form guest list, trimmed + blank-dropped to match what the
  // server stores, compared against the fetched baseline.
  const needsGuestSeed = canManage && activity.allow_plus_one;
  const currentGuestNames = useMemo(
    () =>
      guestNames
        .slice(0, guestCount)
        .map((n) => n.trim())
        .filter((n) => n.length > 0),
    [guestNames, guestCount]
  );
  const guestsChanged = useMemo(() => {
    // Gate on a successful seed: a pending/failed fetch leaves the baseline at [] while the row may
    // hold real guests, so treating any difference as a change there would wipe unseen guests.
    if (!needsGuestSeed || guestSeedStatus !== "loaded") return false;
    const base = baselineGuestNames.map((n) => n.trim());
    if (base.length !== currentGuestNames.length) return true;
    return currentGuestNames.some((n, i) => n !== base[i]);
  }, [needsGuestSeed, guestSeedStatus, baselineGuestNames, currentGuestNames]);
  const guestUpdateSlotIds = useMemo(
    // Kept registrations = held slots still selected (slots being cancelled don't need a guest write).
    () => (guestsChanged ? Array.from(registeredSlotIds).filter((id) => selectedSet.has(id)) : []),
    [guestsChanged, registeredSlotIds, selectedSet]
  );

  const hasChanges = toAddSlotIds.length > 0 || toCancelSlotIds.length > 0 || guestsChanged;

  // Derived values — "+1 guests" won't-fit advisory. Shown when the party (1 + guests) exceeds the
  // seats left on any selected, capacity-limited slot (counts are seat-aware). On a slot the viewer
  // already holds, their current seats are already in counts.registered, so they're added back to
  // measure the room left to *grow* the party into.
  const wontFitWarning = useMemo(() => {
    if (!activity.allow_plus_one || guestCount === 0 || selectedSlotIds.length === 0) return null;
    const partySize = 1 + guestCount;
    const baselineSeats = 1 + baselineGuestNames.length;
    const tight = selectedSlotIds.some((id) => {
      const slot = bookableSlots.find((s) => s.id === id);
      const counts = slotCounts[id];
      if (!slot || slot.capacity === null || !counts) return false;
      const ownSeatsOnSlot = canManage && registeredSlotIds.has(id) ? baselineSeats : 0;
      return slot.capacity - counts.registered + ownSeatsOnSlot < partySize;
    });
    if (!tight) return null;
    return canManage
      ? `Your party of ${partySize} no longer fits the seats left, so confirming will move your registration to the waitlist.`
      : `Your party of ${partySize} may not fit the seats left, so you'll join the waitlist instead.`;
  }, [
    activity.allow_plus_one,
    guestCount,
    selectedSlotIds,
    bookableSlots,
    slotCounts,
    canManage,
    registeredSlotIds,
    baselineGuestNames,
  ]);

  const applyGuestBaseline = useCallback((names: string[]) => {
    setBaselineGuestNames(names);
  }, []);

  // Effects
  // Seed the +1 guests form from the viewer's existing registration when managing. Without this, the
  // form would open at 0 guests and a confirm could silently wipe guests the viewer never saw. The
  // status drives whether guest editing is offered at all (loaded) vs held back (loading/error).
  useEffect(() => {
    if (!needsGuestSeed || seedRegistrationId == null) return;
    // Seed once per registration id; ignore Map-identity churn from parent count refreshes.
    if (seededRegistrationIdRef.current === seedRegistrationId) return;
    seededRegistrationIdRef.current = seedRegistrationId;

    let cancelled = false;
    setGuestSeedStatus("loading");
    getMyRegistrationGuestsFromSession(seedRegistrationId)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setGuestSeedStatus("error");
          showToast("Couldn't load your current guests. Reopen to edit them.", "error");
          return;
        }
        const names = res.guestNames ?? [];
        setBaselineGuestNames(names);
        onGuestsSeeded(names);
        setGuestSeedStatus("loaded");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("useActivityManageDiff: failed to load existing guests", { seedRegistrationId }, error);
        setGuestSeedStatus("error");
        showToast("Couldn't load your current guests. Reopen to edit them.", "error");
      });
    return () => {
      cancelled = true;
    };
  }, [needsGuestSeed, seedRegistrationId, showToast, onGuestsSeeded]);

  return {
    registeredSlotIds,
    canManage,
    needsGuestSeed,
    toAddSlotIds,
    toCancelSlotIds,
    guestsChanged,
    guestUpdateSlotIds,
    hasChanges,
    wontFitWarning,
    baselineGuestNames,
    guestSeedStatus,
    applyGuestBaseline,
  };
}
